import { useState, useEffect } from 'react'
import { useTranslationToggle } from '@/contexts/TranslationContext'

interface UseTranslatableTextOptions {
    sourceLanguage?: string
    enabled?: boolean
}

export function useTranslatableText(
    text: string,
    options: UseTranslatableTextOptions = {}
) {
    const { translateText, isTranslationEnabled } = useTranslationToggle()
    const [translatedText, setTranslatedText] = useState(text)
    const [isLoading, setIsLoading] = useState(false)

    const { sourceLanguage, enabled = true } = options

    // Dynamic/database text is translated ONLY when the translation toggle is on.
    // The UI language selection governs fixed/static text (via i18n), not dynamic content.
    const shouldTranslate = enabled && isTranslationEnabled

    useEffect(() => {
        if (!shouldTranslate || !text || text.trim() === '') {
            setTranslatedText(text)
            return
        }

        const performTranslation = async () => {
            setIsLoading(true)
            try {
                const result = await translateText(text, sourceLanguage)
                setTranslatedText(result)
            } catch (error) {
                console.error('Translation error:', error)
                setTranslatedText(text)
            } finally {
                setIsLoading(false)
            }
        }

        performTranslation()
    }, [text, shouldTranslate, sourceLanguage, translateText])

    return {
        text: translatedText,
        isLoading,
        isTranslated: shouldTranslate && translatedText !== text,
    }
}

// Higher-order component for wrapping translatable content
export function withTranslation<T extends object>(
    Component: React.ComponentType<T>,
    options: UseTranslatableTextOptions = {}
) {
    return function TranslatedComponent(props: T & { text: string }) {
        const { text, ...restProps } = props
        const { text: translatedText, isLoading } = useTranslatableText(text, options)

        return (
            <Component
                {...(restProps as T)}
                text={translatedText}
                isLoading={isLoading}
            />
        )
    }
}











